// === OVERLAY BUG REPORT ===
// Formulaire DOM injecté par-dessus le canvas pour soumettre un bug report.
// Collecte pseudo, description libre, et infos techniques automatiques.

import { PlayerProfile } from '../systems/PlayerProfile';
import { submitBugReportWithAttachments, generateBugReportId } from '../systems/BugReportSender';
import { GAME_VERSION } from '../systems/MetaManager';
import { buildSaveSnapshot, buildLogsBlob } from '../systems/BugReportContext';

/**
 * Affiche le formulaire de bug report.
 * @param currentScene        Clé de la scène Phaser active (affichée dans le rapport).
 * @param onClose             Callback optionnel appelé à la fermeture de l'overlay.
 * @param contextLine         Ligne de contexte run optionnelle (étage, équipe, or...).
 * @param screenshot          Capture PNG du canvas (déjà prise avant l'ouverture de
 *                            l'overlay pour ne pas inclure le formulaire dedans).
 * @param initialDescription  Texte pré-rempli dans la zone de description.
 *                            Utile pour les rapports auto-générés (ex : crash recovery).
 */
export function showBugReportOverlay(
  currentScene: string,
  onClose?: () => void,
  contextLine?: string,
  screenshot?: Blob | null,
  initialDescription?: string,
): void {
  const initialPseudo = PlayerProfile.getPseudo() ?? '';
  // ID pré-généré : affiché à l'utilisateur dans l'overlay et utilisé tel quel
  // par le sender → l'ID montré === l'ID Discord (le testeur peut s'y retrouver).
  const reportId = generateBugReportId();
  const shortId = reportId.split('_').pop() ?? reportId;

  // Fond sombre couvrant tout l'écran
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'width:100%', 'height:100%',
    'background:rgba(0,0,0,0.88)', 'z-index:9999',
    'display:flex', 'align-items:center', 'justify-content:center',
  ].join(';');

  // Bloque les events derrière l'overlay : Phaser écoute keyboard/pointer
  // au niveau document (les events bubblent depuis le textarea/input → document
  // → handler Phaser). On stoppe la propagation en BUBBLE phase au niveau de
  // l'overlay → les enfants (textarea, input) reçoivent normalement l'event
  // en target phase, puis ça remonte → overlay arrête → document/Phaser ne
  // voit rien. Évite : taper SPACE dans la description = endPlayerTurn en
  // combat ; cliquer hors de la box = clic sur tile/skill derrière ; etc.
  const stopAll = (e: Event) => e.stopPropagation();
  ['keydown', 'keyup', 'keypress',
    'mousedown', 'mouseup', 'click',
    'pointerdown', 'pointerup', 'pointermove',
    'touchstart', 'touchend', 'touchmove',
    'wheel'].forEach(type => {
    overlay.addEventListener(type, stopAll);
  });

  // Boîte principale — width responsive (min(420px, 92vw))
  const box = document.createElement('div');
  box.style.cssText = [
    'background:#1a1a2e', 'border:2px solid #e74c3c', 'border-radius:8px',
    'padding:18px 18px', 'width:min(420px, 92vw)', 'box-sizing:border-box',
    'max-height:90vh', 'overflow-y:auto',
    'font-family:monospace', 'color:#ecf0f1',
  ].join(';');

  // Titre
  const title = document.createElement('div');
  title.textContent = '🐛 Signaler un bug';
  title.style.cssText = 'font-size:16px;color:#e74c3c;font-weight:bold;margin-bottom:4px;';

  // Sous-titre
  const subtitle = document.createElement('div');
  subtitle.textContent = 'Décrivez ce qui s\'est passé :';
  subtitle.style.cssText = 'font-size:11px;color:#7f8c8d;margin-bottom:14px;';

  // Pseudo (éditable + auto-saved : la 1ère saisie persiste pour les
  // rapports suivants via PlayerProfile.setPseudo()).
  const pseudoLabel = document.createElement('div');
  pseudoLabel.textContent = 'Ton pseudo de testeur :';
  pseudoLabel.style.cssText = 'font-size:11px;color:#f39c12;margin-bottom:4px;';
  const pseudoInput = document.createElement('input');
  pseudoInput.type = 'text';
  pseudoInput.maxLength = 24;
  pseudoInput.placeholder = 'ex: Capitaine Rouillé';
  pseudoInput.value = initialPseudo;
  pseudoInput.style.cssText = [
    'width:100%', 'box-sizing:border-box',
    'background:#0d1117', 'border:1px solid #3a536b', 'border-radius:4px',
    'color:#f39c12', 'font-family:monospace', 'font-size:12px',
    'padding:6px 10px', 'margin-bottom:12px', 'outline:none',
  ].join(';');

  // Zone de texte — description du bug
  const textarea = document.createElement('textarea');
  textarea.rows = 5;
  textarea.maxLength = 1000;
  textarea.placeholder = 'Décrivez le bug ici (étapes pour le reproduire, comportement attendu, ce qui s\'est passé…)';
  if (initialDescription) {
    textarea.value = initialDescription;
  }
  textarea.style.cssText = [
    'width:100%', 'box-sizing:border-box', 'resize:vertical',
    'background:#0d1117', 'border:1px solid #e74c3c', 'border-radius:4px',
    'color:#ecf0f1', 'font-family:monospace', 'font-size:12px',
    'padding:8px 10px', 'margin-bottom:10px', 'outline:none',
  ].join(';');

  // Infos techniques collectées automatiquement
  const uaShort = navigator.userAgent.length > 65
    ? navigator.userAgent.slice(0, 65) + '…'
    : navigator.userAgent;
  const autoInfo = document.createElement('div');
  autoInfo.style.cssText = 'font-size:10px;color:#444;margin-bottom:14px;line-height:1.7;';
  const lines = [
    `<span style="color:#555">Infos auto :</span>`,
    `<span style="color:#f1c40f">ID&nbsp;: <code>#${shortId}</code></span> <span style="color:#555">(${reportId})</span>`,
    `Version&nbsp;: v${GAME_VERSION}`,
    `Scène&nbsp;: ${currentScene}`,
    `Résolution&nbsp;: ${window.screen.width}×${window.screen.height}`,
    `UA&nbsp;: ${uaShort}`,
  ];
  if (contextLine) lines.push(`Run&nbsp;: ${contextLine}`);
  // Indicateur des pièces jointes (utile pour rassurer le testeur)
  const attachLabels: string[] = [];
  if (screenshot) attachLabels.push('📸 capture');
  attachLabels.push('💾 sauvegarde');
  attachLabels.push('📋 logs');
  lines.push(`<span style="color:#2ecc71">Joints&nbsp;:</span> ${attachLabels.join(' · ')}`);
  autoInfo.innerHTML = lines.join('<br>');

  // Feedback (succès / erreur)
  const feedback = document.createElement('div');
  feedback.style.cssText = 'font-size:11px;margin-bottom:10px;min-height:16px;';

  // Rangée de boutons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:12px;';

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = '[ Annuler ]';
  cancelBtn.style.cssText = [
    'flex:1', 'background:#1e1e1e', 'border:1px solid #555', 'border-radius:4px',
    'color:#7f8c8d', 'font-family:monospace', 'font-size:12px',
    'padding:8px', 'cursor:pointer',
  ].join(';');
  cancelBtn.addEventListener('mouseover', () => { cancelBtn.style.color = '#bdc3c7'; });
  cancelBtn.addEventListener('mouseout', () => { cancelBtn.style.color = '#7f8c8d'; });

  const submitBtn = document.createElement('button');
  submitBtn.textContent = '[ 📤 Envoyer ]';
  submitBtn.style.cssText = [
    'flex:2', 'background:#2a1a1a', 'border:1px solid #e74c3c', 'border-radius:4px',
    'color:#e74c3c', 'font-family:monospace', 'font-size:12px',
    'padding:8px', 'cursor:pointer',
  ].join(';');
  submitBtn.addEventListener('mouseover', () => { submitBtn.style.color = '#ff6b6b'; });
  submitBtn.addEventListener('mouseout', () => { submitBtn.style.color = '#e74c3c'; });

  const close = () => {
    overlay.remove();
    onClose?.();
  };

  cancelBtn.addEventListener('click', close);

  submitBtn.addEventListener('click', async () => {
    const desc = textarea.value.trim();
    if (desc.length < 5) {
      feedback.textContent = 'Veuillez décrire le bug (5 caractères minimum).';
      feedback.style.color = '#e74c3c';
      return;
    }
    // Pseudo : si vide → "Anonyme", sinon trim et persiste pour la prochaine fois
    const pseudoRaw = pseudoInput.value.trim();
    const pseudo = pseudoRaw.length > 0 ? pseudoRaw : 'Anonyme';
    if (pseudoRaw.length > 0 && pseudoRaw !== initialPseudo) {
      PlayerProfile.setPseudo(pseudoRaw);
    }

    submitBtn.disabled = true;
    submitBtn.textContent = '[ … envoi en cours ]';
    feedback.textContent = '';

    // Snapshot save + logs au moment de l'envoi (l'utilisateur peut avoir tapé
    // sa description plusieurs minutes après avoir vu le bug → l'état n'a pas
    // bougé entre-temps, mais on prend la photo la plus fraîche disponible).
    const saveBlob = buildSaveSnapshot();
    const logsBlob = buildLogsBlob();

    const result = await submitBugReportWithAttachments(
      {
        pseudo,
        description: desc,
        version: GAME_VERSION,
        userAgent: navigator.userAgent,
        resolution: `${window.screen.width}×${window.screen.height}`,
        scene: currentScene,
        context: contextLine,
      },
      {
        screenshot: screenshot ?? null,
        save: saveBlob,
        logs: logsBlob,
      },
      reportId,
    );

    feedback.textContent = result.message;
    feedback.style.color = result.sentToDiscord ? '#2ecc71' : (result.ok ? '#f39c12' : '#e74c3c');

    if (result.ok) {
      // Fermeture automatique après confirmation
      setTimeout(close, 2200);
    } else {
      // Anti-spam : réactiver pour permettre re-essai
      submitBtn.disabled = false;
      submitBtn.textContent = '[ 📤 Envoyer ]';
    }
  });

  btnRow.appendChild(cancelBtn);
  btnRow.appendChild(submitBtn);

  box.appendChild(title);
  box.appendChild(subtitle);
  box.appendChild(pseudoLabel);
  box.appendChild(pseudoInput);
  box.appendChild(textarea);
  box.appendChild(autoInfo);
  box.appendChild(feedback);
  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  // Focus : pseudo si vide (1ère fois), sinon directement la description
  setTimeout(() => {
    if (initialPseudo.length === 0) pseudoInput.focus();
    else textarea.focus();
  }, 50);
}
